import { lazy } from "react";
import { createBrowserRouter, type RouteObject } from "react-router-dom";

import { LoginPage } from "@/features/auth/LoginPage";
import { SetPasswordPage } from "@/features/auth/SetPasswordPage";
import { InventoryPage } from "@/features/inventory/InventoryPage";
import { SalesPage } from "@/features/sales/SalesPage";
import { CustomersPage } from "@/features/customers/CustomersPage";
import { SuppliersPage } from "@/features/suppliers/SuppliersPage";
import { ProcurementPage } from "@/features/procurement/ProcurementPage";
import { DashboardPage } from "@/features/dashboard/DashboardPage";
import { ReportsPage } from "@/features/reports/ReportsPage";
import { ExpensesPage } from "@/features/expenses/ExpensesPage";
import { KitchenPage } from "@/features/kitchen/KitchenPage";
import { AuditLogPage } from "@/features/audit/AuditLogPage";
import { SyncCentrePage } from "@/features/sync/SyncCentrePage";
import { SettingsPage } from "@/features/settings/SettingsPage";
import { BillingPage } from "@/features/billing/BillingPage";
import { MarketplacePage } from "@/features/marketplace/MarketplacePage";
import { ApplyPage } from "@/features/apply/ApplyPage";
import { ApplicationStatusPage } from "@/features/apply/ApplicationStatusPage";
import { EasyGuidePage } from "@/features/guide/easy/EasyGuidePage";
import { GuidePage } from "@/features/guide/GuidePage";
import { ManualPage } from "@/features/guide/ManualPage";
import { NotFoundPage } from "@/features/placeholder/NotFoundPage";
import { PlaceholderPage } from "@/features/placeholder/PlaceholderPage";
import {
  PublicStokvelPage,
  OldStokfairAddress,
  StokkieHome,
  StokkieLayout,
  StokkiePricing,
  StokkieSafety,
  StokvelDirectory,
} from "@/features/stokkie/StokkieSite";
import { PhoneSignInPage } from "@/features/stokvel/PhoneSignInPage";
import {
  RequireStokvelSession,
  StokvelLayout,
} from "@/features/stokvel/StokvelLayout";

import {
  HomeRedirect,
  RequireAnonymous,
  RequireAuth,
  RequirePermission,
  RequirePlatformStaff,
  RequireShopAccess,
} from "./guards";
import { AppShell } from "./layout/AppShell";
import { ALL_ROUTES } from "./routes";
import { RouteError } from "./RouteError";

/**
 * The route tree.
 *
 * Built from the same manifest that drives the sidebar, so a screen can never
 * appear in the navigation without a route, or gain a route nobody can reach.
 *
 * Every path currently renders the placeholder. As each phase lands, one line
 * here changes from `<PlaceholderPage />` to the real screen — the shell, the
 * navigation and the header need no edit at all.
 *
 * `createBrowserRouter` rather than hash routing because the PWA is served from
 * its own origin and clean paths matter for the printed QR receipts in Phase 7.
 */
/** Built screens, by path. Everything else falls back to the placeholder. */
const SCREENS: Record<string, React.ReactNode> = {
  "/inventory": <InventoryPage />,
  "/sales": <SalesPage />,
  "/customers": <CustomersPage />,
  "/suppliers": <SuppliersPage />,
  "/procurement": <ProcurementPage />,
  "/dashboard": <DashboardPage />,
  "/reports": <ReportsPage />,
  "/expenses": <ExpensesPage />,
  "/kitchen": <KitchenPage />,
  "/sync": <SyncCentrePage />,
  "/audit": <AuditLogPage />,
  "/settings": <SettingsPage />,
  "/billing": <BillingPage />,
  "/marketplace": <MarketplacePage />,
};

// The administration portal, lazily loaded. A cashier's tablet on a prepaid
// bundle must never download the admin bundle to open a till, and platform staff
// are a handful of people — so this is the one route tree in the application that
// is worth a separate chunk.
const AdminLayout = lazy(() =>
  import("@/features/admin/AdminLayout").then((m) => ({
    default: m.AdminLayout,
  })),
);
const AdminChunk = lazy(() =>
  import("@/features/admin/AdminLayout").then((m) => ({
    default: m.AdminChunk,
  })),
);
const AdminDashboardPage = lazy(() =>
  import("@/features/admin/DashboardPage").then((m) => ({
    default: m.AdminDashboardPage,
  })),
);
const ApplicationsPage = lazy(() =>
  import("@/features/admin/ApplicationsPage").then((m) => ({
    default: m.ApplicationsPage,
  })),
);
const AdminMerchantsPage = lazy(() =>
  import("@/features/admin/MerchantsPage").then((m) => ({
    default: m.MerchantsPage,
  })),
);
const MerchantDetailPage = lazy(() =>
  import("@/features/admin/MerchantDetailPage").then((m) => ({
    default: m.MerchantDetailPage,
  })),
);
const PackagesPage = lazy(() =>
  import("@/features/admin/PackagesPage").then((m) => ({
    default: m.PackagesPage,
  })),
);
const PackageRequestsPage = lazy(() =>
  import("@/features/admin/PackageRequestsPage").then((m) => ({
    default: m.PackageRequestsPage,
  })),
);
const InvoicesPage = lazy(() =>
  import("@/features/admin/InvoicesPage").then((m) => ({
    default: m.InvoicesPage,
  })),
);
const AdminStaffPage = lazy(() =>
  import("@/features/admin/StaffPage").then((m) => ({ default: m.StaffPage })),
);
const AdminProfilePage = lazy(() =>
  import("@/features/admin/ProfilePage").then((m) => ({
    default: m.ProfilePage,
  })),
);
const AdminStokvelsPage = lazy(() =>
  import("@/features/admin/StokvelsPage").then((m) => ({
    default: m.StokvelsPage,
  })),
);
const AdminAuditPage = lazy(() =>
  import("@/features/admin/AuditPage").then((m) => ({ default: m.AuditPage })),
);

// The stokvel portal, a chunk of its own for the opposite reason to the admin
// portal: its members are on entry-level phones and prepaid data, and must not
// download the till to see who has paid this month.
const StokvelHomePage = lazy(() =>
  import("@/features/stokvel/StokvelHomePage").then((m) => ({
    default: m.StokvelHomePage,
  })),
);
const StartStokvelPage = lazy(() =>
  import("@/features/stokvel/StartStokvelPage").then((m) => ({
    default: m.StartStokvelPage,
  })),
);
const JoinStokvelPage = lazy(() =>
  import("@/features/stokvel/JoinStokvelPage").then((m) => ({
    default: m.JoinStokvelPage,
  })),
);
const StartBulkOrderPage = lazy(() =>
  import("@/features/stokvel/StartBulkOrderPage").then((m) => ({
    default: m.StartBulkOrderPage,
  })),
);
const BulkOrderPage = lazy(() =>
  import("@/features/stokvel/BulkOrderPage").then((m) => ({
    default: m.BulkOrderPage,
  })),
);
const InboxPage = lazy(() =>
  import("@/features/stokvel/InboxPage").then((m) => ({
    default: m.InboxPage,
  })),
);
const GroupMarketPage = lazy(() =>
  import("@/features/stokvel/GroupMarketPage").then((m) => ({
    default: m.GroupMarketPage,
  })),
);
const GroupSellPage = lazy(() =>
  import("@/features/stokvel/GroupMarketPage").then((m) => ({
    default: m.GroupSellPage,
  })),
);
const GroupOrdersPage = lazy(() =>
  import("@/features/stokvel/GroupMarketPage").then((m) => ({
    default: m.GroupOrdersPage,
  })),
);
const GroupOrderPage = lazy(() =>
  import("@/features/stokvel/GroupMarketPage").then((m) => ({
    default: m.GroupOrderPage,
  })),
);
const MarketPage = lazy(() =>
  import("@/features/stokvel/MarketPage").then((m) => ({
    default: m.MarketPage,
  })),
);
const ListingPage = lazy(() =>
  import("@/features/stokvel/ListingPage").then((m) => ({
    default: m.ListingPage,
  })),
);
const SellPage = lazy(() =>
  import("@/features/stokvel/SellPage").then((m) => ({ default: m.SellPage })),
);
const MyListingsPage = lazy(() =>
  import("@/features/stokvel/MyListingsPage").then((m) => ({
    default: m.MyListingsPage,
  })),
);
const ConversationsPage = lazy(() =>
  import("@/features/stokvel/ConversationsPage").then((m) => ({
    default: m.ConversationsPage,
  })),
);
const ConversationPage = lazy(() =>
  import("@/features/stokvel/ConversationPage").then((m) => ({
    default: m.ConversationPage,
  })),
);
const SellerPage = lazy(() =>
  import("@/features/stokvel/SellerPage").then((m) => ({
    default: m.SellerPage,
  })),
);
const StokvelGroupPage = lazy(() =>
  import("@/features/stokvel/StokvelGroupPage").then((m) => ({
    default: m.StokvelGroupPage,
  })),
);
const MemberHistoryPage = lazy(() =>
  import("@/features/stokvel/StokvelGroupPage").then((m) => ({
    default: m.MemberHistoryPage,
  })),
);
const StokvelSectionPage = lazy(() =>
  import("@/features/stokvel/StokvelGroupPage").then((m) => ({
    default: m.StokvelSectionPage,
  })),
);

/**
 * Every top-level route gets the same error screen, so no failure — a release
 * that replaced the file a screen needs, above all — reaches React Router's
 * developer page. Errors in child routes bubble up to their parent's.
 */
const withErrorScreen = (routes: RouteObject[]): RouteObject[] =>
  routes.map((route) => ({ errorElement: <RouteError />, ...route }));

export const router = createBrowserRouter(
  withErrorScreen([
    {
      path: "/admin",
      element: (
        <RequirePlatformStaff>
          <AdminChunk>
            <AdminLayout />
          </AdminChunk>
        </RequirePlatformStaff>
      ),
      children: [
        {
          index: true,
          element: (
            <AdminChunk>
              <AdminDashboardPage />
            </AdminChunk>
          ),
        },
        {
          path: "applications",
          element: (
            <AdminChunk>
              <ApplicationsPage />
            </AdminChunk>
          ),
        },
        {
          path: "merchants",
          element: (
            <AdminChunk>
              <AdminMerchantsPage />
            </AdminChunk>
          ),
        },
        {
          path: "merchants/:id",
          element: (
            <AdminChunk>
              <MerchantDetailPage />
            </AdminChunk>
          ),
        },
        {
          path: "packages",
          element: (
            <AdminChunk>
              <PackagesPage />
            </AdminChunk>
          ),
        },
        {
          path: "package-requests",
          element: (
            <AdminChunk>
              <PackageRequestsPage />
            </AdminChunk>
          ),
        },
        {
          path: "invoices",
          element: (
            <AdminChunk>
              <InvoicesPage />
            </AdminChunk>
          ),
        },
        {
          path: "staff",
          element: (
            <AdminChunk>
              <AdminStaffPage />
            </AdminChunk>
          ),
        },
        {
          path: "profile",
          element: (
            <AdminChunk>
              <AdminProfilePage />
            </AdminChunk>
          ),
        },
        {
          path: "audit",
          element: (
            <AdminChunk>
              <AdminAuditPage />
            </AdminChunk>
          ),
        },
        {
          path: "stokvels",
          element: (
            <AdminChunk>
              <AdminStokvelsPage />
            </AdminChunk>
          ),
        },
      ],
    },
    {
      path: "/login",
      element: (
        <RequireAnonymous>
          <LoginPage />
        </RequireAnonymous>
      ),
    },
    // The front door, and the only routes here with no guard at all. An applicant
    // has no account — that is what they are applying for — so `RequireAnonymous`
    // would be wrong too: a shop owner already using SmartSpaza may perfectly well
    // be applying on behalf of a second business.
    { path: "/apply", element: <ApplyPage /> },
    { path: "/apply/status", element: <ApplicationStatusPage /> },
    // Read at a till that is not paired yet, so there is nobody to sign in as.
    { path: "/guide", element: <GuidePage /> },
    { path: "/guide/:slug", element: <ManualPage /> },
    { path: "/help", element: <EasyGuidePage /> },
    { path: "/help/:task", element: <EasyGuidePage /> },
    // Unguarded for the same reason: the emailed link is the credential, and
    // somebody signed in on a shared back-office browser must still be able to
    // use a link meant for them.
    { path: "/set-password", element: <SetPasswordPage /> },
    // "Register as a Stokvel": cellphone and one-time PIN. Unguarded, like the
    // application form — it is how somebody without an account gets one.
    { path: "/stokvel/start", element: <PhoneSignInPage /> },
    // The Stokkie Stokvel website: public, no sign-in, its own frame.
    {
      path: "/stokkie",
      element: <StokkieLayout />,
      children: [
        { index: true, element: <StokkieHome /> },
        { path: "stokvels", element: <StokvelDirectory /> },
        { path: "s/:slug", element: <PublicStokvelPage /> },
        { path: "pricing", element: <StokkiePricing /> },
        { path: "safety", element: <StokkieSafety /> },
      ],
    },
    // The website's address before the rename; shared links still land.
    { path: "/stokfair/*", element: <OldStokfairAddress /> },
    { path: "/stokfair", element: <OldStokfairAddress /> },
    {
      path: "/stokvel",
      element: (
        <RequireStokvelSession>
          <StokvelLayout />
        </RequireStokvelSession>
      ),
      children: [
        { index: true, element: <StokvelHomePage /> },
        { path: "new", element: <StartStokvelPage /> },
        { path: "join", element: <JoinStokvelPage /> },
        { path: "inbox", element: <InboxPage /> },
        { path: "market", element: <MarketPage /> },
        { path: "market/sell", element: <SellPage /> },
        { path: "market/mine", element: <MyListingsPage /> },
        { path: "market/groups", element: <GroupMarketPage /> },
        { path: "market/groups/sell", element: <GroupSellPage /> },
        { path: "market/groups/orders", element: <GroupOrdersPage /> },
        { path: "market/groups/orders/:orderId", element: <GroupOrderPage /> },
        { path: "market/:listingId", element: <ListingPage /> },
        { path: "messages", element: <ConversationsPage /> },
        { path: "messages/:enquiryId", element: <ConversationPage /> },
        { path: "sellers/:userId", element: <SellerPage /> },
        { path: ":stokvelId", element: <StokvelGroupPage /> },
        { path: ":stokvelId/bulk/new", element: <StartBulkOrderPage /> },
        { path: ":stokvelId/bulk/:orderId", element: <BulkOrderPage /> },
        {
          path: ":stokvelId/member/:membershipId",
          element: <MemberHistoryPage />,
        },
        { path: ":stokvelId/:section", element: <StokvelSectionPage /> },
      ],
    },
    {
      path: "/",
      element: (
        <RequireAuth>
          <RequireShopAccess>
            <AppShell />
          </RequireShopAccess>
        </RequireAuth>
      ),
      children: [
        // Sales rather than the dashboard: a till is what most people open this
        // for, and the dashboard has nothing to show until Phase 9.
        { index: true, element: <HomeRedirect /> },

        // Screens that exist. Phase 3 adds Inventory, Phase 4 Sales, and so on —
        // one entry each; the guard and the shell do not change.
        ...ALL_ROUTES.map((route) => {
          const screen = SCREENS[route.path] ?? <PlaceholderPage />;
          return {
            path: route.path.slice(1),
            element: route.permission ? (
              <RequirePermission
                permission={route.permission}
                supportPermission={route.supportPermission}
              >
                {screen}
              </RequirePermission>
            ) : (
              screen
            ),
          };
        }),

        { path: "*", element: <NotFoundPage /> },
      ],
    },
  ]),
);
